import { useCallback } from 'react';
import { createStableObjectHash, type PointerModifierState } from '@/storybook/components/FormCollection/utils/utils';

export type MultiSelectOptionClickModifiers = PointerModifierState;

const getValueKey = (valueToCheck: unknown) =>
  valueToCheck !== null && typeof valueToCheck === 'object'
    ? createStableObjectHash(valueToCheck)
    : String(valueToCheck);

type UseMultiSelectHandlersProps<T> = {
  value: Array<T>;
  onChange: (selectedValue?: Array<T>) => void;
  onClear?: VoidFunction;
  disabled?: boolean;
  clearable?: boolean;
  isSelected: (value: T) => boolean;
};

export const useMultiSelectHandlersFloatingLabel = <T>({
  value,
  onChange,
  onClear,
  disabled,
  clearable,
  isSelected,
}: UseMultiSelectHandlersProps<T>) => {
  const handleClearValue = useCallback(() => {
    if (disabled || !clearable) return;
    if (onClear) {
      onClear();
    } else {
      onChange([]);
    }
  }, [onClear, disabled, clearable, onChange]);

  const handleOptionClick = useCallback(
    (clickedValue: T, currentScopeOptions: T[], clickedIndex: number, modifiers: MultiSelectOptionClickModifiers) => {
      if (disabled) return;

      if (!modifiers.shiftKey) {
        const clickedKey = getValueKey(clickedValue);
        const nextValue = isSelected(clickedValue)
          ? value.filter(existingValue => getValueKey(existingValue) !== clickedKey)
          : [...value, clickedValue];
        onChange(nextValue);
        return;
      }

      const findNearestSelectedIndex = (): number => {
        const optionsBefore = currentScopeOptions.slice(0, clickedIndex).reverse();
        const backwardIndex = optionsBefore.findIndex(option => isSelected(option));

        if (backwardIndex !== -1) {
          return clickedIndex - 1 - backwardIndex;
        }

        const optionsAfter = currentScopeOptions.slice(clickedIndex + 1);
        const forwardIndex = optionsAfter.findIndex(option => isSelected(option));

        return forwardIndex !== -1 ? clickedIndex + 1 + forwardIndex : -1;
      };

      const anchorIndex = findNearestSelectedIndex();
      const startIndex = anchorIndex === -1 ? 0 : Math.min(anchorIndex, clickedIndex);
      const endIndex = anchorIndex === -1 ? clickedIndex : Math.max(anchorIndex, clickedIndex);

      const rangeOptions = currentScopeOptions.slice(startIndex, endIndex + 1);
      const newSelections = rangeOptions.filter(option => !isSelected(option));

      const hasNewSelections = newSelections.length > 0;
      if (hasNewSelections) {
        onChange([...value, ...newSelections]);
      }
    },
    [disabled, isSelected, value, onChange],
  );

  return {
    handleClearValue,
    handleOptionClick,
  };
};
